import React from "react";
import { TopBar } from "@/components/TopBar";
import { apiListStatusOrder, formatApiErrorDetail } from "@/lib/api";
import { Loader2, RefreshCw } from "lucide-react";
import GarisProgres from "@/components/GarisProgres";

// Order status for CS / kasir / owner. The endpoint never returns prices or payments.
const STEP_STYLE = {
  PENDING: "border-slate-600 text-slate-400",
  IN_PROGRESS: "border-amber-500 bg-amber-500 text-slate-950 font-semibold",
  COMPLETED: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
};

export default function StatusOrder() {
  const [rows, setRows] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [kasir, setKasir] = React.useState("");  // owner 2026-10-15 (A1)
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setRows(await apiListStatusOrder()); setError(null); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const needle = q.trim().toLowerCase();
  const daftarKasir = [...new Map(rows.filter((o) => o.cs_id).map((o) => [o.cs_id, o.cs_nama || "-"])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const shown = rows.filter((o) => (!kasir || o.cs_id === kasir) && (!needle || [o.nomor_order, o.nomor_faktur, o.customer?.nama,
    o.customer?.kode_customer, ...o.spks.map((s) => s.nomor_spk)].some((v) => (v || "").toLowerCase().includes(needle))));

  return (
    <div data-testid="status-order-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Status Order</h1>
          <p className="text-sm text-slate-400 mt-1">Posisi pengerjaan setiap order, untuk menjawab pertanyaan customer.</p>
        </div>
        {error && <div data-testid="status-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
        <div className="flex flex-wrap items-center gap-3">
          <input data-testid="status-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nomor order / faktur / SPK / customer"
            className="w-full max-w-md rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60" />
          <select data-testid="status-filter-kasir" value={kasir} onChange={(e) => setKasir(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-sm">
            <option value="">Semua Kasir/CS</option>{daftarKasir.map(([id, nama]) => <option key={id} value={id}>{nama}</option>)}
          </select>
          <button data-testid="status-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
        </div>
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
        ) : (
          <div className="space-y-3" data-testid="status-list">
            {shown.map((o) => (
              <div key={o.id} data-testid={`status-order-${o.nomor_order}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <span className="font-mono text-amber-300">{o.nomor_faktur || o.nomor_order}</span>
                  <span className="text-slate-200">{o.customer?.nama || "—"}</span>
                  <span className="font-mono text-xs text-slate-500">{o.customer?.kode_customer || ""} {o.customer?.nomor_telepon || ""}</span>
                  {o.cs_nama && <span data-testid={`status-cs-${o.nomor_order}`} className="text-xs text-slate-400">CS: {o.cs_nama}</span>}
                  <span className="ml-auto text-xs font-mono text-slate-400">{o.status_order}</span>
                </div>
                <div className="mt-1 text-xs text-slate-400">{o.items.map((it) => `${it.nama || "-"} × ${it.quantity} ${it.satuan || ""}`).join(" · ")}</div>
                {o.progres && <div className="mt-2 max-w-xl"><GarisProgres progres={o.progres} kecil testid={`status-garis-${o.nomor_order}`} /></div>}
                {o.spks.length === 0 && <div className="mt-2 text-xs text-slate-500">Belum ada SPK.</div>}
                {o.spks.map((s) => (
                  <div key={s.id} className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-slate-300 w-40">{s.nomor_spk}{s.jenis_mesin_kode ? ` · ${s.jenis_mesin_kode}` : ""}</span>
                    <span data-testid={`status-spk-${s.nomor_spk}`} className="text-xs font-semibold text-cyan-300 w-36">{s.status_spk === "READY" || s.status_spk === "CANCELLED" ? s.status_produksi : "Belum masuk produksi"}</span>
                    {s.langkah.map((l, i) => (
                      <span key={i} className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STEP_STYLE[l.status] || STEP_STYLE.PENDING}`}>{l.pos_nama || l.pos_code}</span>
                    ))}
                  </div>
                ))}
              </div>
            ))}
            {shown.length === 0 && <div className="text-center text-slate-500 py-8">Tidak ada order.</div>}
          </div>
        )}
      </main>
    </div>
  );
}
